import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Scale, Eye, EyeOff, ArrowLeft, Loader2 } from 'lucide-react';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import shared from '@/styles/public.module.css';
import formStyles from '@/styles/form.module.css';
import shell from '@/styles/authShell.module.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The invite email exists to put one case in front of this attorney, so both
// exits from this page carry its id. LawyerDashboard reads the id off
// location.pathname; an id we never got would build a route matching no
// application, so fall back to the plain dashboard rather than send them
// somewhere broken. Module scope keeps it out of the effect's deps.
const reviewRoute = (applicationId) =>
  applicationId ? `/lawyer/application/${applicationId}` : '/lawyer';

export default function AttorneyInvite() {
  usePageMeta(PAGE_META.attorneyInvite);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError('No invite token provided.'); setLoading(false); return; }
    fetch(`${API}/attorney-invite/${token}`)
      .then(async (r) => { const data = await r.json(); if (!r.ok) throw new Error(data.detail || 'Invalid invite'); return data; })
      .then(data => {
        setInvite(data); setName(data.attorney_name || ''); setEmail(data.attorney_email || '');
        if (data.has_account) {
          navigate(`/login?redirect=${reviewRoute(data.application_id)}&invite=${token}`, { replace: true });
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, navigate]);

  const handleSignup = async () => {
    setFormError('');
    if (!name.trim()) { setFormError('Name is required'); return; }
    if (!email.trim()) { setFormError('Email is required'); return; }
    if (password.length < 8) { setFormError('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword) { setFormError('Passwords do not match'); return; }
    if (!/[A-Z]/.test(password) || !/[0-9]/.test(password)) { setFormError('Include at least one uppercase letter and one number'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/attorney-invite/signup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ name, email, password, invite_token: token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Signup failed');
      localStorage.setItem('access_token', data.access_token);
      navigate(reviewRoute(data.application_id), { replace: true });
    } catch (err) { setFormError(err.message); }
    finally { setSubmitting(false); }
  };

  const Header = ({ withBack = false }) => (
    <header className={shell.header}>
      <div className={shell.headerInner}>
        <button type="button" className={shell.logo} onClick={() => navigate('/')} aria-label="MyCaseLoan home">
          <Scale size={26} aria-hidden="true" />
          <span className={shell.logoText}>MyCaseLoan</span>
        </button>
        {withBack && (
          <button type="button" className={`${shared.btn} ${shared.btnQuiet}`} onClick={() => navigate('/login')}>
            <ArrowLeft size={15} aria-hidden="true" /> Login
          </button>
        )}
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className={shell.page}>
        <main className={shell.centre}>
          <Loader2 size={32} className={shell.spinner} aria-hidden="true" />
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className={shell.page}>
        <Header />
        <main className={shell.centre}>
          <div className={shell.inner}>
            <div className={shell.card}>
              <div className={shell.head}>
                <div className={`${shell.statusIcon} ${shell.statusError}`}>
                  <Scale size={32} aria-hidden="true" />
                </div>
                <h1 className={shell.title}>Invalid Invite Link</h1>
                <p className={shell.subtitle}>{error}</p>
              </div>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
                onClick={() => navigate('/login')}
              >
                Go to Login
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={shell.page} data-testid="attorney-invite-page">
      <Header withBack />

      <main className={shell.centre}>
        <div className={shell.inner}>
          <div className={shell.card} data-testid="attorney-invite-form">
            <div className={shell.head}>
              <div className={shell.mark}>
                <Scale size={28} aria-hidden="true" />
              </div>
              <h1 className={shell.title}>You&apos;ve Been Invited</h1>
              <p className={shell.subtitle}>
                Your client <strong>{invite?.client_name}</strong> submitted a funding
                application that needs your review.
              </p>
            </div>

            <div className={shell.banner}>
              Create your attorney account to review and manage this application.
            </div>

            <div className={formStyles.field}>
              <label className={formStyles.label} htmlFor="invite-name">Full Name *</label>
              <input id="invite-name" data-testid="invite-name" className={formStyles.control}
                value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" />
            </div>
            <div className={formStyles.field}>
              <label className={formStyles.label} htmlFor="invite-email">Email *</label>
              <input id="invite-email" data-testid="invite-email" type="email" className={formStyles.control}
                value={email} onChange={e => setEmail(e.target.value)} placeholder="attorney@lawfirm.com" />
            </div>
            <div className={formStyles.field}>
              <label className={formStyles.label} htmlFor="invite-password">Password *</label>
              <div className={formStyles.passwordWrap}>
                <input id="invite-password" data-testid="invite-password"
                  type={showPassword ? 'text' : 'password'} className={formStyles.control}
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Min 8 chars, 1 uppercase, 1 number" />
                <button type="button" className={formStyles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}>
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>
            <div className={formStyles.field}>
              <label className={formStyles.label} htmlFor="invite-confirm">Confirm Password *</label>
              <input id="invite-confirm" data-testid="invite-confirm"
                type={showPassword ? 'text' : 'password'} className={formStyles.control}
                value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password" />
            </div>

            {formError && <p className={formStyles.error} data-testid="invite-error">{formError}</p>}

            <button data-testid="invite-submit-btn" type="button" onClick={handleSignup} disabled={submitting}
              className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn} ${shell.spacedTop}`}>
              {submitting ? 'Creating Account...' : 'Create Account & Review Application'}
            </button>

            <p className={shell.altText}>
              Already have an account?{' '}
              <button type="button" className={shell.link} onClick={() => navigate('/login')}>Log in here</button>
            </p>
          </div>

          <p className={shell.footNote}>
            &copy; {new Date().getFullYear()} MyCaseLoan. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  );
}
